import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import './AdminDashboardLayout.css';
import wsService from '../utils/websocketService';
import API_CONFIG from '../config/api';
import { extendSession, isSessionValid } from '../utils/sessionManager';
import { useBranding, useDocumentBranding, legalSubline } from '../utils/loginBranding';
// Warms a tab's code chunk as soon as the pointer lands on its sidebar link
// — by the time an intentional hover turns into a click, the chunk is
// usually already cached, so the tab opens with no visible loading step.
import { preloadTabChunk } from './admin/tabRegistry';
import ExportOtpProvider from './admin/ExportOtpModal';
import { logAdminActivity } from '../utils/adminActivity';

// Sidebar navigation icons — consistent Lucide-style line icons.
// They inherit `currentColor`, so hover/active colouring is handled entirely by CSS.
const NAV_ICONS = {
    dashboard: (<><rect width="7" height="9" x="3" y="3" rx="1" /><rect width="7" height="5" x="14" y="3" rx="1" /><rect width="7" height="9" x="14" y="12" rx="1" /><rect width="7" height="5" x="3" y="16" rx="1" /></>),
    globalSearch: (<><circle cx="11" cy="11" r="8" /><line x1="21" x2="16.65" y1="21" y2="16.65" /></>),
    analytics: (<><path d="M3 3v18h18" /><path d="m19 9-5 5-4-4-3 3" /></>),
    investor: (<><path d="M3 21h18" /><path d="M5 21V9l7-5 7 5v12" /><path d="M9 21v-7h6v7" /><path d="M9 10h.01" /><path d="M15 10h.01" /></>),
    admins: (<><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" /><path d="m9 12 2 2 4-4" /></>),
    users: (<><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>),
    customer: (<><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></>),
    kyc: (<><rect width="20" height="14" x="2" y="5" rx="2" /><circle cx="9" cy="11" r="2" /><path d="M6.5 15a3 3 0 0 1 5 0" /><path d="M16 10h2" /><path d="M16 14h2" /></>),
    loans: (<><path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4" /><path d="M4 6v12c0 1.1.9 2 2 2h14v-4" /><path d="M18 12a2 2 0 0 0-2 2c0 1.1.9 2 2 2h4v-4z" /></>),
    creditExec: (<><rect width="8" height="4" x="8" y="2" rx="1" ry="1" /><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" /><path d="M12 11h4" /><path d="M12 16h4" /><path d="M8 11h.01" /><path d="M8 16h.01" /></>),
    creditHead: (<><path d="M16 20V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" /><rect width="20" height="14" x="2" y="6" rx="2" /></>),
    deepReview: (<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h8" /><path d="M14 2v4a2 2 0 0 0 2 2h4" /><circle cx="16" cy="17" r="3" /><path d="m21 21-1.5-1.5" /></>),
    esign: (<><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" /><path d="m15 5 3 3" /></>),
    disbursal: (<><path d="M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11z" /><path d="m21.854 2.147-10.94 10.939" /></>),
    collection: (<><line x1="3" x2="21" y1="22" y2="22" /><line x1="6" x2="6" y1="18" y2="11" /><line x1="10" x2="10" y1="18" y2="11" /><line x1="14" x2="14" y1="18" y2="11" /><line x1="18" x2="18" y1="18" y2="11" /><polygon points="12 2 20 7 4 7" /></>),
    reloan: (<><path d="M21 12a9 9 0 0 0-15.5-6.2L3 8" /><path d="M3 3v5h5" /><path d="M3 12a9 9 0 0 0 15.5 6.2L21 16" /><path d="M21 21v-5h-5" /></>),
    closed: (<><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></>),
    leads: (<><circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="6" /><circle cx="12" cy="12" r="2" /></>),
    salesLeads: (<><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" /></>),
    whatsapp: (<><path d="M21 11.5a8.38 8.38 0 0 1-12.1 7.48L3 21l2.02-5.72A8.38 8.38 0 1 1 21 11.5z" /><path d="M8.5 8.5c.3 3.4 2.6 5.7 6 6" /><path d="M9 8h.01" /><path d="M15 15h.01" /></>),
    sanction: (<><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7z" /><path d="M14 2v4a2 2 0 0 0 2 2h4" /><path d="M10 9H8" /><path d="M16 13H8" /><path d="M16 17H8" /></>),
    crif: (<><path d="m12 14 4-4" /><path d="M3.34 19a10 10 0 1 1 17.32 0" /></>),
    aadhaar: (<><path d="M12 10a2 2 0 0 0-2 2c0 1.02-.1 2.51-.26 4" /><path d="M14 13.12c0 2.38 0 6.38-1 8.88" /><path d="M17.29 21.02c.12-.6.43-2.3.5-3.02" /><path d="M2 12a10 10 0 0 1 18-6" /><path d="M2 16h.01" /><path d="M21.8 16c.2-2 .131-5.354 0-6" /><path d="M5 19.5C5.5 18 6 15 6 12a6 6 0 0 1 .34-2" /><path d="M8.65 22c.21-.66.45-1.32.57-2" /><path d="M9 6.8a6 6 0 0 1 9 5.2v2" /></>),
    bank: (<><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7z" /><path d="M14 2v4a2 2 0 0 0 2 2h4" /><path d="M8 13h2" /><path d="M14 13h2" /><path d="M8 17h2" /><path d="M14 17h2" /></>),
    bre: (<><line x1="6" x2="6" y1="3" y2="15" /><circle cx="18" cy="6" r="3" /><circle cx="6" cy="18" r="3" /><path d="M18 9a9 9 0 0 1-9 9" /></>),
    permissions: (<><circle cx="7.5" cy="15.5" r="5.5" /><path d="m21 2-9.6 9.6" /><path d="m15.5 7.5 3 3L22 7l-3-3" /></>),
    roles: (<><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><polyline points="16 11 18 13 22 9" /></>),
    settings: (<><path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" /><circle cx="12" cy="12" r="3" /></>),
    database: (<><ellipse cx="12" cy="5" rx="9" ry="3" /><path d="M3 5V19A9 3 0 0 0 21 19V5" /><path d="M3 12A9 3 0 0 0 21 12" /></>),
    api: (<><polyline points="4 17 10 11 4 5" /><line x1="12" x2="20" y1="19" y2="19" /></>),
    payments: (<><rect width="20" height="14" x="2" y="5" rx="2" /><line x1="2" x2="22" y1="10" y2="10" /></>),
    activity: (<><path d="M22 12h-2.48a2 2 0 0 0-1.93 1.46l-2.35 8.36a.25.25 0 0 1-.48 0L9.24 2.18a.25.25 0 0 0-.48 0l-2.35 8.36A2 2 0 0 1 4.49 12H2" /></>),
    logs: (<><path d="M15 12h-5" /><path d="M15 8h-5" /><path d="M19 17V5a2 2 0 0 0-2-2H4" /><path d="M8 21h12a2 2 0 0 0 2-2v-1a1 1 0 0 0-1-1H11a1 1 0 0 0-1 1v1a2 2 0 1 1-4 0V5a2 2 0 1 0-4 0v2a1 1 0 0 0 1 1h3" /></>),
    reports: (<><path d="M3 3v18h18" /><path d="M18 17V9" /><path d="M13 17V5" /><path d="M8 17v-3" /></>),
    support: (<><circle cx="12" cy="12" r="10" /><path d="m4.93 4.93 4.24 4.24" /><path d="m14.83 9.17 4.24-4.24" /><path d="m14.83 14.83 4.24 4.24" /><path d="m9.17 14.83-4.24 4.24" /><circle cx="12" cy="12" r="4" /></>),
    brand: (<><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" /></>),
    myDashboard: (<><polyline points="22 7 13.5 15.5 8.5 10.5 2 17" /><polyline points="16 7 22 7 22 13" /></>),
    targets: (<><circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="6" /><circle cx="12" cy="12" r="2" /></>)
};

function NavIcon({ name }) {
    return (
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            {NAV_ICONS[name] || NAV_ICONS.dashboard}
        </svg>
    );
}

function AdminDashboardLayout() {
    const navigate = useNavigate();
    const location = useLocation();
    const [showLogoutWarning, setShowLogoutWarning] = useState(false);
    const [showProfile, setShowProfile] = useState(false);
    const [adminSession, setAdminSession] = useState(null);
    const profileDropdownRef = useRef(null);

    const [showChangePassword, setShowChangePassword] = useState(false);
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    const [changePasswordError, setChangePasswordError] = useState('');
    const [changePasswordSuccess, setChangePasswordSuccess] = useState('');
    const [isChangingPassword, setIsChangingPassword] = useState(false);

    const [referralLink, setReferralLink] = useState(null);
    const [referralLinkError, setReferralLinkError] = useState('');
    const [referralCopied, setReferralCopied] = useState(false);

    const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
        try { return localStorage.getItem('adminSidebarCollapsed') === 'true'; }
        catch (e) { return false; }
    });
    const performanceDashboardRoles = ['credit_executive', 'credit_head', 'sales_executive', 'sales_head', 'collection_executive', 'collection_head'];
    const isPerformanceDashboardRole = performanceDashboardRoles.includes(adminSession?.role);
    const toggleSidebar = () => {
        setSidebarCollapsed((prev) => {
            const next = !prev;
            try { localStorage.setItem('adminSidebarCollapsed', String(next)); } catch (e) { /* ignore */ }
            return next;
        });
    };

    // Brand mark, name and browser-tab icon follow Settings → Login Page Branding.
    const brand = useBranding();
    useDocumentBranding(brand, `${brand.brandName} Admin Portal`);
    const currentTab = location.pathname.startsWith('/admin/customer/')
        ? 'customerProfile'
        : location.pathname === '/admin/customers'
        ? 'customer'
        : location.pathname === '/admin/dashboard'
        ? (isPerformanceDashboardRole ? 'myDashboard' : 'dashboard')
        : new URLSearchParams(location.search).get('tab') || 'dashboard';

    const hasPermission = (permission) => {
        if (adminSession?.role === 'super_admin') return true;
        if (Array.isArray(permission)) {
            return permission.some(p => adminSession?.permissions?.includes(p));
        }
        return adminSession?.permissions?.includes(permission);
    };

    const activeTabTitle = {
        dashboard: 'Dashboard',
        investorDashboard: 'Investor Dashboard',
        collectionReport: 'Collection Report',
        analytics: 'Analytics & Insights',
        admins: 'Admin Management',
        users: 'User Management',
        customer: 'Customer Management',
        customerProfile: 'Customer Information',
        kyc: 'KYC Central',
        loans: 'Loan Management',
        leads: 'Lead Management',
        salesLeads: 'Sales Leads',
        permissions: 'Permissions Control',
        roles: 'Role Management',
        settings: 'System Settings',
        database: 'Database Management',
        sanctionLetters: 'Sanction Letters',
        crifData: 'CRIF Data',
        crifJson: 'CRIF JSON',
        crifHtml: 'CRIF HTML',
        aadhaarInfo: 'Aadhaar Info',
        bankStatements: 'Client Bank Statements',
        breDecision: 'BRE Decisions',
        activity: 'Activity Monitor',
        logs: 'Audit Logs',
        reports: 'Reports & Analytics',
        support: 'Support',
        payments: 'PayU',
        api: 'API Management',
        'queue-credit-exec': 'Credit Executive Queue',
        'queue-credit-head': 'Credit Head Queue',
        'queue-deep-review': 'Deep Review Queue',
        'queue-esign': 'eSign Queue',
        'queue-disbursal': 'Disbursal Queue',
        'queue-collection': 'Collection Queue',
        'queue-reloan': 'Reloan Queue',
        'queue-closed': 'Closed Loans',
        bankAndTransactions: 'Bank and Transactions',
        whatsappMarketing: 'WhatsApp',
        globalSearch: 'Global Search',
        myDashboard: isPerformanceDashboardRole ? 'Dashboard' : 'My Dashboard',
        staffTargets: 'Targets'
    }[currentTab] || 'Dashboard';

    // The browsing trail: which page this admin opened. A customer's own page
    // is recorded by the server, with the customer, when the profile loads.
    useEffect(() => {
        if (!adminSession?.userId || currentTab === 'customerProfile') return;
        logAdminActivity('page_viewed', `Opened ${activeTabTitle}`);
    }, [adminSession?.userId, currentTab, activeTabTitle]);

    useEffect(() => {
        // Load admin session
        const parsedSession = getStoredAdminSession();
        if (parsedSession) {
            setAdminSession(parsedSession);

            // Redirect regular users (non-staff) to the user dashboard. Any
            // role other than the plain customer 'user' role is staff — a
            // fixed whitelist here previously went stale (e.g. it never knew
            // about credit_executive/credit_head/sales_executive/etc.), which
            // logged those real staff straight back out on every page load.
            if (!parsedSession.role || parsedSession.role === 'user') {
                console.warn('Non-admin user attempting to access admin portal');
                navigate('/dashboard');
                return;
            }
        } else {
            // No session, redirect to login
            navigate('/admin/login');
            return;
        }

        // Listen for session updates (e.g. permission changes)
        const handleAdminUpdate = (payload) => {
            const currentSession = getStoredAdminSession();
            if (payload && payload.adminId === currentSession?.userId) {
                console.log('🔄 Layout: Syncing session changes');
                const current = currentSession;
                const updated = {
                    ...current,
                    permissions: payload.permissions || current.permissions,
                    role: payload.role || current.role,
                    level: payload.level || current.level,
                    name: payload.name || current.name
                };
                // Persist + sync so refreshed pages immediately reflect new permissions
                localStorage.setItem('adminSession', JSON.stringify(updated));
                setAdminSession(updated);
            }
        };

        wsService.on('admin.updated', handleAdminUpdate);
        return () => wsService.off('admin.updated', handleAdminUpdate);
    }, [navigate]);

    // Establish WebSocket for real-time permission sync (all admin roles)
    useEffect(() => {
        if (!adminSession?.userId) return;

        wsService.connect(adminSession.userId, true)
            .catch(err => console.error('WS connect error (layout):', err));

        return () => {
            try { wsService.disconnect(); } catch (e) { /* ignore */ }
        };
    }, [adminSession?.userId]);

    // Keep the session alive while the admin is actually working. Without
    // this, expiresAt is set once at login and never moves — sessionManager's
    // extendSession() existed but nothing ever called it, so every admin got
    // logged out exactly SESSION_DURATION after signing in regardless of how
    // actively they were using the panel (and reliably mid-task, since the
    // click/keypress that would have extended the session is also what
    // triggers the re-render where ProtectedAdminRoute notices it's expired).
    useEffect(() => {
        if (!adminSession) return undefined;

        let lastExtend = 0;
        const EXTEND_THROTTLE_MS = 60 * 1000; // at most once a minute

        const handleActivity = () => {
            const now = Date.now();
            if (now - lastExtend < EXTEND_THROTTLE_MS) return;
            lastExtend = now;
            if (!isSessionValid()) {
                navigate('/admin/login', { replace: true });
                return;
            }
            extendSession();
        };

        const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart'];
        activityEvents.forEach((evt) => document.addEventListener(evt, handleActivity, { passive: true }));

        // Also catch pure idle expiry (a tab left open with no interaction at
        // all) rather than waiting for the next click to discover it.
        const idleCheck = setInterval(() => {
            if (!isSessionValid()) {
                navigate('/admin/login', { replace: true });
            }
        }, 30 * 1000);

        return () => {
            activityEvents.forEach((evt) => document.removeEventListener(evt, handleActivity));
            clearInterval(idleCheck);
        };
    }, [adminSession, navigate]);

    useEffect(() => {
        if (!showProfile) return undefined;

        const handleOutsideClick = (event) => {
            if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
                setShowProfile(false);
            }
        };

        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, [showProfile]);

    useEffect(() => {
        if (!showProfile || adminSession?.role !== 'credit_executive' || referralLink) return;

        fetch(`${API_CONFIG.BASE_URL}/api/admin/my-referral-link`, {
            headers: { 'x-user-id': adminSession?.userId }
        })
            .then((res) => res.json())
            .then((data) => {
                if (data.success && data.data) setReferralLink(data.data);
                else setReferralLinkError('Could not load your referral link');
            })
            .catch(() => setReferralLinkError('Could not load your referral link'));
    }, [showProfile, adminSession, referralLink]);

    const copyReferralLink = () => {
        if (!referralLink?.url) return;
        navigator.clipboard.writeText(referralLink.url).then(() => {
            setReferralCopied(true);
            setTimeout(() => setReferralCopied(false), 2000);
        });
    };

    const handleLogout = () => {
        // Clear all session data
        localStorage.clear();
        sessionStorage.clear();

        // Clear browser history to prevent back button access
        window.history.pushState(null, '', '/admin/login');

        // Disable back button after logout
        window.onpopstate = function () {
            window.history.pushState(null, '', '/admin/login');
        };

        // Force page reload to clear any cached state and navigate to admin login
        window.location.replace('/admin/login');
    };

    const confirmLogout = () => {
        setShowLogoutWarning(false);
        handleLogout();
    };

    const cancelLogout = () => {
        setShowLogoutWarning(false);
    };

    const openChangePassword = () => {
        setShowProfile(false);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmNewPassword('');
        setChangePasswordError('');
        setChangePasswordSuccess('');
        setShowChangePassword(true);
    };

    const closeChangePassword = () => {
        setShowChangePassword(false);
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();
        setChangePasswordError('');

        if (newPassword.length < 8) {
            setChangePasswordError('New password must be at least 8 characters');
            return;
        }
        if (newPassword !== confirmNewPassword) {
            setChangePasswordError('New passwords do not match');
            return;
        }

        setIsChangingPassword(true);
        try {
            const res = await fetch(`${API_CONFIG.BASE_URL}/api/admin/change-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'x-user-id': adminSession?.userId },
                body: JSON.stringify({ currentPassword, newPassword })
            });
            const data = await res.json();

            if (!res.ok || !data.success) {
                setChangePasswordError(data.message || 'Could not change password');
                return;
            }

            setChangePasswordSuccess('Password updated successfully.');
            setCurrentPassword('');
            setNewPassword('');
            setConfirmNewPassword('');
        } catch (error) {
            setChangePasswordError('Failed to reach the server.');
        } finally {
            setIsChangingPassword(false);
        }
    };

    const getRoleConfig = () => {
        const role = adminSession?.role;
        const roleLabels = {
            'super_admin': { label: 'Super Admin', class: 'super-admin' },
            'admin': { label: 'Admin', class: 'admin' },
            'sales': { label: 'Sales', class: 'sales' },
            'support': { label: 'Support', class: 'support' },
            'collection': { label: 'Collections', class: 'support' },
            'branch': { label: 'Branch', class: 'admin' },
            'accounts': { label: 'Accounts', class: 'sales' },
            // credit/sales/collection executive+head roles (staff_targets'
            // ROLE_METRIC_MAP on the backend) were missing here, so anyone
            // holding one fell through to the generic 'Staff' default below —
            // e.g. a credit_executive's own dashboard subtitle correctly said
            // "Credit Executive" while the profile badge next to it said "Staff".
            'credit_executive': { label: 'Credit Executive', class: 'admin' },
            'credit_head': { label: 'Credit Head', class: 'admin' },
            'sales_executive': { label: 'Sales Executive', class: 'sales' },
            'sales_head': { label: 'Sales Head', class: 'sales' },
            'collection_executive': { label: 'Collection Executive', class: 'support' },
            'collection_head': { label: 'Collection Head', class: 'support' }
        };
        return roleLabels[role] || { label: 'Staff', class: 'admin' };
    };

    const getProfileChipLabel = () => {
        if (adminSession?.role === 'super_admin') {
            return 'Full Access';
        }
        return getRoleConfig().label;
    };

    const getAdminInitials = () => {
        const name = adminSession?.name || 'Admin';
        return name
            .split(' ')
            .filter(Boolean)
            .slice(0, 2)
            .map((part) => part[0])
            .join('')
            .toUpperCase();
    };

    const getStoredAdminSession = () => {
        const session = localStorage.getItem('adminSession');
        if (!session) return null;

        try {
            return JSON.parse(session);
        } catch (error) {
            console.error('Invalid admin session in layout:', error);
            localStorage.removeItem('adminSession');
            return null;
        }
    };

    return (
        <div className="admin-dashboard-layout">
            {/* Sidebar Navigation */}
            <aside className={`admin-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
                <button
                    type="button"
                    className="sidebar-toggle-btn"
                    onClick={toggleSidebar}
                    aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                    title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                >
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        {sidebarCollapsed ? <polyline points="9 18 15 12 9 6" /> : <polyline points="15 18 9 12 15 6" />}
                    </svg>
                </button>
                <div className="admin-sidebar-header" onClick={() => navigate('/admin/super-dashboard')}>
                    <div className="admin-brand-mark">
                        {brand.ready && <img src={brand.logoSrc} alt={brand.brandName} />}
                    </div>
                    <div className="admin-brand-copy">
                        <span className="admin-brand-name">{brand.brandName}</span>
                        {legalSubline(brand.brandName, brand.legalName) && (
                            <span className="admin-brand-sub">{legalSubline(brand.brandName, brand.legalName)}</span>
                        )}
                    </div>
                </div>

                <div className="admin-sidebar-menu">
                    <div className="admin-sidebar-panel admin-sidebar-panel-grouped">
                        <div className="admin-sidebar-section">
                            <div className="menu-section-label">Main Menu</div>
                            <button
                                className={`sidebar-menu-item ${(currentTab === 'dashboard' || (isPerformanceDashboardRole && currentTab === 'myDashboard')) ? 'active' : ''}`}
                                onClick={() => navigate(isPerformanceDashboardRole ? '/admin/super-dashboard?tab=myDashboard' : '/admin/dashboard')}
                                onMouseEnter={() => isPerformanceDashboardRole && preloadTabChunk('myDashboard')}
                            >
                                <span className="menu-icon"><NavIcon name="dashboard" /></span>
                                <span>Dashboard</span>
                            </button>

                            {hasPermission('investor.dashboard') && (
                                <button
                                    className={`sidebar-menu-item ${currentTab === 'investorDashboard' ? 'active' : ''}`}
                                    onClick={() => navigate('/admin/super-dashboard?tab=investorDashboard')}
                                    onMouseEnter={() => preloadTabChunk('investorDashboard')}
                                >
                                    <span className="menu-icon"><NavIcon name="investor" /></span>
                                    <span>Investor Dashboard</span>
                                </button>
                            )}

                            {hasPermission('investor.dashboard') && (
                                <button
                                    className={`sidebar-menu-item ${currentTab === 'collectionReport' ? 'active' : ''}`}
                                    onClick={() => navigate('/admin/super-dashboard?tab=collectionReport')}
                                    onMouseEnter={() => preloadTabChunk('collectionReport')}
                                >
                                    <span className="menu-icon"><NavIcon name="collection" /></span>
                                    <span>Collection Report</span>
                                </button>
                            )}

                            {hasPermission('search.view') && (
                                <button
                                    className={`sidebar-menu-item ${currentTab === 'globalSearch' ? 'active' : ''}`}
                                    onClick={() => navigate('/admin/super-dashboard?tab=globalSearch')}
                                    onMouseEnter={() => preloadTabChunk('globalSearch')}
                                >
                                    <span className="menu-icon"><NavIcon name="globalSearch" /></span>
                                    <span>Global Search</span>
                                </button>
                            )}

                            {hasPermission('analytics.view') && (
                                <button
                                    className={`sidebar-menu-item ${currentTab === 'analytics' ? 'active' : ''}`}
                                    onClick={() => navigate('/admin/super-dashboard?tab=analytics')}
                                    onMouseEnter={() => preloadTabChunk('analytics')}
                                >
                                    <span className="menu-icon"><NavIcon name="analytics" /></span>
                                    <span>Analytics</span>
                                </button>
                            )}
                        </div>

                        <div className="admin-sidebar-section">
                            <div className="menu-section-label">Management</div>
                            {hasPermission('admins.view_all') && (
                                <button
                                    className={`sidebar-menu-item ${currentTab === 'admins' ? 'active' : ''}`}
                                    onClick={() => navigate('/admin/super-dashboard?tab=admins')}
                                    onMouseEnter={() => preloadTabChunk('admins')}
                                >
                                    <span className="menu-icon"><NavIcon name="admins" /></span>
                                    <span>Admins</span>
                                </button>
                            )}
                            {adminSession?.role === 'super_admin' && (
                                <button
                                    className={`sidebar-menu-item ${currentTab === 'staffTargets' ? 'active' : ''}`}
                                    onClick={() => navigate('/admin/super-dashboard?tab=staffTargets')}
                                    onMouseEnter={() => preloadTabChunk('staffTargets')}
                                >
                                    <span className="menu-icon"><NavIcon name="targets" /></span>
                                    <span>Targets</span>
                                </button>
                            )}
                            {hasPermission('users.view_all') && (
                                <button
                                    className={`sidebar-menu-item ${currentTab === 'customer' ? 'active' : ''}`}
                                    onClick={() => navigate('/admin/customers')}
                                >
                                    <span className="menu-icon"><NavIcon name="customer" /></span>
                                    <span>Customer</span>
                                </button>
                            )}
                            {hasPermission('kyc.manage') && (
                                <button
                                    className={`sidebar-menu-item ${currentTab === 'kyc' ? 'active' : ''}`}
                                    onClick={() => navigate('/admin/super-dashboard?tab=kyc')}
                                    onMouseEnter={() => preloadTabChunk('kyc')}
                                >
                                    <span className="menu-icon"><NavIcon name="kyc" /></span>
                                    <span>KYC Management</span>
                                </button>
                            )}
                            {hasPermission(['loans.view_all', 'loans.view']) && (
                                <>
                                    <button
                                        className={`sidebar-menu-item ${currentTab === 'loans' ? 'active' : ''}`}
                                        onClick={() => navigate('/admin/super-dashboard?tab=loans')}
                                        onMouseEnter={() => preloadTabChunk('loans')}
                                    >
                                        <span className="menu-icon"><NavIcon name="loans" /></span>
                                        <span>Loans</span>
                                    </button>
                                    <button
                                        className={`sidebar-menu-item ${currentTab === 'queue-credit-exec' ? 'active' : ''}`}
                                        onClick={() => navigate('/admin/super-dashboard?tab=queue-credit-exec')}
                                        onMouseEnter={() => preloadTabChunk('queue-credit-exec')}
                                    >
                                        <span className="menu-icon"><NavIcon name="creditExec" /></span>
                                        <span>Credit Exec Queue</span>
                                    </button>
                                </>
                            )}

                            {hasPermission('leads.view') && (
                                <>
                                    <button
                                        className={`sidebar-menu-item ${currentTab === 'leads' ? 'active' : ''}`}
                                        onClick={() => navigate('/admin/super-dashboard?tab=leads')}
                                        onMouseEnter={() => preloadTabChunk('leads')}
                                    >
                                        <span className="menu-icon"><NavIcon name="leads" /></span>
                                        <span>Leads</span>
                                    </button>
                                    <button
                                        className={`sidebar-menu-item ${currentTab === 'salesLeads' ? 'active' : ''}`}
                                        onClick={() => navigate('/admin/super-dashboard?tab=salesLeads')}
                                        onMouseEnter={() => preloadTabChunk('salesLeads')}
                                    >
                                        <span className="menu-icon"><NavIcon name="salesLeads" /></span>
                                        <span>Sales Leads</span>
                                    </button>
                                </>
                            )}

                            {/* Credit Head Queue and Disbursal Queue are the credit_head/final-approval
                                stages — scoped to the roles that actually own those stages (plus
                                admin/super_admin), or to anyone individually granted the matching
                                queue.credit_head / queue.disbursal permission (see EditAdminModal's
                                "Queue Access" category) so a super admin can hand a queue to e.g. a
                                credit_executive without changing their role. */}
                            {(['credit_head', 'admin', 'super_admin'].includes(adminSession?.role) || hasPermission('queue.credit_head')) && (
                                <button
                                    className={`sidebar-menu-item ${currentTab === 'queue-credit-head' ? 'active' : ''}`}
                                    onClick={() => navigate('/admin/super-dashboard?tab=queue-credit-head')}
                                    onMouseEnter={() => preloadTabChunk('queue-credit-head')}
                                >
                                    <span className="menu-icon"><NavIcon name="creditHead" /></span>
                                    <span>Credit Head Queue</span>
                                </button>
                            )}
                            {(['credit_head', 'admin', 'super_admin'].includes(adminSession?.role) || hasPermission('queue.disbursal')) && (
                                <button
                                    className={`sidebar-menu-item ${currentTab === 'queue-esign' ? 'active' : ''}`}
                                    onClick={() => navigate('/admin/super-dashboard?tab=queue-esign')}
                                    onMouseEnter={() => preloadTabChunk('queue-esign')}
                                >
                                    <span className="menu-icon"><NavIcon name="esign" /></span>
                                    <span>eSign Queue</span>
                                </button>
                            )}
                            {(['credit_head', 'admin', 'super_admin'].includes(adminSession?.role) || hasPermission('queue.disbursal')) && (
                                <button
                                    className={`sidebar-menu-item ${currentTab === 'queue-disbursal' ? 'active' : ''}`}
                                    onClick={() => navigate('/admin/super-dashboard?tab=queue-disbursal')}
                                    onMouseEnter={() => preloadTabChunk('queue-disbursal')}
                                >
                                    <span className="menu-icon"><NavIcon name="disbursal" /></span>
                                    <span>Disbursal Queue</span>
                                </button>
                            )}
                            {(['credit_head', 'admin', 'super_admin'].includes(adminSession?.role) || hasPermission('queue.disbursal')) && (
                                <button
                                    className={`sidebar-menu-item ${currentTab === 'bankAndTransactions' ? 'active' : ''}`}
                                    onClick={() => navigate('/admin/super-dashboard?tab=bankAndTransactions')}
                                    onMouseEnter={() => preloadTabChunk('bankAndTransactions')}
                                >
                                    <span className="menu-icon"><NavIcon name="bank" /></span>
                                    <span>Bank & Transactions</span>
                                </button>
                            )}
                            {(['collection_head', 'collection_executive', 'admin', 'super_admin'].includes(adminSession?.role) || hasPermission('queue.collection')) && (
                                <button
                                    className={`sidebar-menu-item ${currentTab === 'queue-collection' ? 'active' : ''}`}
                                    onClick={() => navigate('/admin/super-dashboard?tab=queue-collection')}
                                    onMouseEnter={() => preloadTabChunk('queue-collection')}
                                >
                                    <span className="menu-icon"><NavIcon name="collection" /></span>
                                    <span>Collection Queue</span>
                                </button>
                            )}
                            {(adminSession?.role === 'super_admin' || adminSession?.role === 'admin' || hasPermission('queue.reloan')) && (
                                <button
                                    className={`sidebar-menu-item ${currentTab === 'queue-reloan' ? 'active' : ''}`}
                                    onClick={() => navigate('/admin/super-dashboard?tab=queue-reloan')}
                                    onMouseEnter={() => preloadTabChunk('queue-reloan')}
                                >
                                    <span className="menu-icon"><NavIcon name="reloan" /></span>
                                    <span>Reloan Queue</span>
                                </button>
                            )}
                            {/* Closed Loans has its own gate, separate from Collection Queue's —
                                credit_executive can see whether a loan they processed has closed
                                without also getting the Collection Queue's collection actions (see
                                the matching queue.closed grant in ROLE_PERMISSION_GRANTS,
                                backend/server.js). */}
                            {(['collection_head', 'collection_executive', 'credit_executive', 'admin', 'super_admin'].includes(adminSession?.role) || hasPermission('queue.collection') || hasPermission('queue.closed')) && (
                                <button
                                    className={`sidebar-menu-item ${currentTab === 'queue-closed' ? 'active' : ''}`}
                                    onClick={() => navigate('/admin/super-dashboard?tab=queue-closed')}
                                    onMouseEnter={() => preloadTabChunk('queue-closed')}
                                >
                                    <span className="menu-icon"><NavIcon name="closed" /></span>
                                    <span>Closed Loans</span>
                                </button>
                            )}
                            {/* Deep Review is an escalation bucket normally only Admin/Super Admin
                                resolve (see CustomerJourneyPage's approval panel), so its queue
                                link is scoped to those two roles, or to anyone individually granted
                                queue.deep_review. */}
                            {(adminSession?.role === 'super_admin' || adminSession?.role === 'admin' || hasPermission('queue.deep_review')) && (
                                <button
                                    className={`sidebar-menu-item ${currentTab === 'queue-deep-review' ? 'active' : ''}`}
                                    onClick={() => navigate('/admin/super-dashboard?tab=queue-deep-review')}
                                    onMouseEnter={() => preloadTabChunk('queue-deep-review')}
                                >
                                    <span className="menu-icon"><NavIcon name="deepReview" /></span>
                                    <span>Deep Review Queue</span>
                                </button>
                            )}

                        </div>

                        {hasPermission(['api.manage', 'whatsapp.manage']) && (
                            <div className="admin-sidebar-section">
                                <div className="menu-section-label">Marketing</div>
                                <button
                                    className={`sidebar-menu-item ${currentTab === 'whatsappMarketing' ? 'active' : ''}`}
                                    onClick={() => navigate('/admin/super-dashboard?tab=whatsappMarketing')}
                                    onMouseEnter={() => preloadTabChunk('whatsappMarketing')}
                                >
                                    <span className="menu-icon"><NavIcon name="whatsapp" /></span>
                                    <span>WhatsApp</span>
                                </button>
                            </div>
                        )}

                        {hasPermission('database.manage') && (
                            <div className="admin-sidebar-section">
                                <div className="menu-section-label">Client Data</div>
                                <button
                                    className={`sidebar-menu-item ${currentTab === 'sanctionLetters' ? 'active' : ''}`}
                                    onClick={() => navigate('/admin/super-dashboard?tab=sanctionLetters')}
                                    onMouseEnter={() => preloadTabChunk('sanctionLetters')}
                                >
                                    <span className="menu-icon"><NavIcon name="sanction" /></span>
                                    <span>Sanction Letters</span>
                                </button>
                                <button
                                    className={`sidebar-menu-item ${currentTab === 'crifData' ? 'active' : ''}`}
                                    onClick={() => navigate('/admin/super-dashboard?tab=crifData')}
                                    onMouseEnter={() => preloadTabChunk('crifData')}
                                >
                                    <span className="menu-icon"><NavIcon name="crif" /></span>
                                    <span>CRIF Data</span>
                                </button>
                                <button
                                    className={`sidebar-menu-item ${currentTab === 'crifJson' ? 'active' : ''}`}
                                    onClick={() => navigate('/admin/super-dashboard?tab=crifJson')}
                                    onMouseEnter={() => preloadTabChunk('crifJson')}
                                >
                                    <span className="menu-icon"><NavIcon name="database" /></span>
                                    <span>CRIF JSON</span>
                                </button>
                                <button
                                    className={`sidebar-menu-item ${currentTab === 'crifHtml' ? 'active' : ''}`}
                                    onClick={() => navigate('/admin/super-dashboard?tab=crifHtml')}
                                    onMouseEnter={() => preloadTabChunk('crifHtml')}
                                >
                                    <span className="menu-icon"><NavIcon name="reports" /></span>
                                    <span>CRIF HTML</span>
                                </button>
                                <button
                                    className={`sidebar-menu-item ${currentTab === 'aadhaarInfo' ? 'active' : ''}`}
                                    onClick={() => navigate('/admin/super-dashboard?tab=aadhaarInfo')}
                                    onMouseEnter={() => preloadTabChunk('aadhaarInfo')}
                                >
                                    <span className="menu-icon"><NavIcon name="aadhaar" /></span>
                                    <span>Aadhaar Info</span>
                                </button>
                                <button
                                    className={`sidebar-menu-item ${currentTab === 'bankStatements' ? 'active' : ''}`}
                                    onClick={() => navigate('/admin/super-dashboard?tab=bankStatements')}
                                    onMouseEnter={() => preloadTabChunk('bankStatements')}
                                >
                                    <span className="menu-icon"><NavIcon name="bank" /></span>
                                    <span>Bank Statements</span>
                                </button>
                                <button
                                    className={`sidebar-menu-item ${currentTab === 'breDecision' ? 'active' : ''}`}
                                    onClick={() => navigate('/admin/super-dashboard?tab=breDecision')}
                                    onMouseEnter={() => preloadTabChunk('breDecision')}
                                >
                                    <span className="menu-icon"><NavIcon name="bre" /></span>
                                    <span>BRE Decision</span>
                                </button>
                            </div>
                        )}
                        {[
                            'permissions.manage',
                            'roles.manage',
                            'settings.manage',
                            'database.manage',
                            'activity.view',
                            'logs.view',
                            'reports.view',
                            'tickets.manage',
                            'payments.manage',
                            'api.manage'
                        ].some(hasPermission) && (
                            <div className="admin-sidebar-section">
                                <div className="menu-section-label">System</div>
                                {hasPermission('permissions.manage') && (
                                    <button
                                        className={`sidebar-menu-item ${currentTab === 'permissions' ? 'active' : ''}`}
                                        onClick={() => navigate('/admin/super-dashboard?tab=permissions')}
                                        onMouseEnter={() => preloadTabChunk('permissions')}
                                    >
                                        <span className="menu-icon"><NavIcon name="permissions" /></span>
                                        <span>Permissions</span>
                                    </button>
                                )}
                                {hasPermission('roles.manage') && (
                                    <button
                                        className={`sidebar-menu-item ${currentTab === 'roles' ? 'active' : ''}`}
                                        onClick={() => navigate('/admin/super-dashboard?tab=roles')}
                                        onMouseEnter={() => preloadTabChunk('roles')}
                                    >
                                        <span className="menu-icon"><NavIcon name="roles" /></span>
                                        <span>Roles</span>
                                    </button>
                                )}
                                {hasPermission('settings.manage') && (
                                    <button
                                        className={`sidebar-menu-item ${currentTab === 'settings' ? 'active' : ''}`}
                                        onClick={() => navigate('/admin/super-dashboard?tab=settings')}
                                        onMouseEnter={() => preloadTabChunk('settings')}
                                    >
                                        <span className="menu-icon"><NavIcon name="settings" /></span>
                                        <span>Settings</span>
                                    </button>
                                )}
                                {hasPermission('database.manage') && (
                                    <button
                                        className={`sidebar-menu-item ${currentTab === 'database' ? 'active' : ''}`}
                                        onClick={() => navigate('/admin/super-dashboard?tab=database')}
                                        onMouseEnter={() => preloadTabChunk('database')}
                                    >
                                        <span className="menu-icon"><NavIcon name="database" /></span>
                                        <span>Database</span>
                                    </button>
                                )}
                                {hasPermission('api.manage') && (
                                    <button
                                        className={`sidebar-menu-item ${currentTab === 'api' ? 'active' : ''}`}
                                        onClick={() => navigate('/admin/super-dashboard?tab=api')}
                                        onMouseEnter={() => preloadTabChunk('api')}
                                    >
                                        <span className="menu-icon"><NavIcon name="api" /></span>
                                        <span>API Management</span>
                                    </button>
                                )}
                                {hasPermission('payments.manage') && (
                                    <button
                                        className={`sidebar-menu-item ${currentTab === 'payments' ? 'active' : ''}`}
                                        onClick={() => navigate('/admin/super-dashboard?tab=payments')}
                                        onMouseEnter={() => preloadTabChunk('payments')}
                                    >
                                        <span className="menu-icon"><NavIcon name="payments" /></span>
                                        <span>PayU</span>
                                    </button>
                                )}
                            </div>
                        )}

                        <div className="admin-sidebar-section">
                            <div className="menu-section-label">Monitoring</div>
                            {hasPermission('activity.view') && (
                                <button
                                    className={`sidebar-menu-item ${currentTab === 'activity' ? 'active' : ''}`}
                                    onClick={() => navigate('/admin/super-dashboard?tab=activity')}
                                    onMouseEnter={() => preloadTabChunk('activity')}
                                >
                                    <span className="menu-icon"><NavIcon name="activity" /></span>
                                    <span>Activity Monitor</span>
                                </button>
                            )}
                            {hasPermission('logs.view') && (
                                <button
                                    className={`sidebar-menu-item ${currentTab === 'logs' ? 'active' : ''}`}
                                    onClick={() => navigate('/admin/super-dashboard?tab=logs')}
                                    onMouseEnter={() => preloadTabChunk('logs')}
                                >
                                    <span className="menu-icon"><NavIcon name="logs" /></span>
                                    <span>Audit Logs</span>
                                </button>
                            )}
                            {hasPermission('reports.view') && (
                                <button
                                    className={`sidebar-menu-item ${currentTab === 'reports' ? 'active' : ''}`}
                                    onClick={() => navigate('/admin/super-dashboard?tab=reports')}
                                    onMouseEnter={() => preloadTabChunk('reports')}
                                >
                                    <span className="menu-icon"><NavIcon name="reports" /></span>
                                    <span>Reports</span>
                                </button>
                            )}
                            {hasPermission('tickets.manage') && (
                                <button
                                    className={`sidebar-menu-item ${currentTab === 'support' ? 'active' : ''}`}
                                    onClick={() => navigate('/admin/super-dashboard?tab=support')}
                                    onMouseEnter={() => preloadTabChunk('support')}
                                >
                                    <span className="menu-icon"><NavIcon name="support" /></span>
                                    <span>Support</span>
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </aside>

            {/* Main Content Area */}
            <div className="admin-main-container">
                {/* Top Header */}
                <header className="admin-top-header">
                    <div className="header-left">
                        <div className="header-title-stack">
                            <h1 className="active-tab-title">
                                {activeTabTitle}
                            </h1>
                            <span className="header-subtitle">Admin workspace</span>
                        </div>
                    </div>
                    <div className="header-right">
                        <button
                            className="admin-nav-icon-btn"
                            title="Notifications"
                            type="button"
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M15 17h5l-1.4-1.4A2 2 0 0 1 18 14.2V11a6 6 0 0 0-12 0v3.2a2 2 0 0 1-.6 1.4L4 17h5"></path>
                                <path d="M10 21a2 2 0 0 0 4 0"></path>
                            </svg>
                        </button>
                        <div className="admin-profile-dropdown-wrap" ref={profileDropdownRef}>
                            <button
                                className="admin-user-chip"
                                title="Profile"
                                type="button"
                                onClick={() => setShowProfile(!showProfile)}
                            >
                                <span className="admin-user-chip-avatar">{getAdminInitials()}</span>
                                <span className="admin-user-chip-copy">
                                    <strong>{adminSession?.name || 'Admin'}</strong>
                                    <span className={`role-badge ${getRoleConfig().class}`}>{getProfileChipLabel()}</span>
                                </span>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="m6 9 6 6 6-6"></path>
                                </svg>
                            </button>

                            {/* Profile Dropdown */}
                            {showProfile && adminSession && (
                                <div className="admin-dropdown-panel admin-profile-panel">
                                    <div className="admin-profile-header">
                                        <div className="admin-profile-avatar">
                                            {adminSession.name ? adminSession.name.split(' ').map(n => n[0]).join('') : 'AD'}
                                        </div>
                                        <div className="admin-profile-info">
                                            <h3>{adminSession.name || 'Admin'}</h3>
                                            <p>{adminSession.email}</p>
                                        </div>
                                    </div>
                                    <div className="admin-profile-details">
                                        <div className="admin-detail-row">
                                            <span className="admin-detail-label">Role</span>
                                            <span className="admin-detail-value">
                                            {adminSession.role === 'super_admin' ? 'Super Administrator' :
                                                adminSession.role === 'sales' ? 'Sales Representative' :
                                                    adminSession.role === 'support' ? 'Support Executive' :
                                                        adminSession.role === 'collection' ? 'Collections' :
                                                            adminSession.role === 'branch' ? 'Branch Manager' :
                                                                adminSession.role === 'accounts' ? 'Accounts Ops' : 'Administrator'}
                                            </span>
                                        </div>
                                        <div className="admin-detail-row">
                                            <span className="admin-detail-label">Permissions</span>
                                            <span className="admin-detail-value">
                                                {adminSession.role === 'super_admin' ? 'Full System Access' : `${adminSession.permissions?.length || 0} Features Active`}
                                            </span>
                                        </div>
                                    </div>
                                    {adminSession.role === 'credit_executive' && (
                                        <div className="admin-profile-referral">
                                            <span className="admin-detail-label">Your Referral Link</span>
                                            {referralLink ? (
                                                <div className="admin-referral-link-row">
                                                    <input
                                                        type="text"
                                                        readOnly
                                                        value={referralLink.url}
                                                        onClick={(e) => e.target.select()}
                                                        className="admin-referral-link-input"
                                                    />
                                                    <button
                                                        type="button"
                                                        className="admin-profile-action-btn"
                                                        onClick={copyReferralLink}
                                                    >
                                                        {referralCopied ? 'Copied!' : 'Copy'}
                                                    </button>
                                                </div>
                                            ) : (
                                                <span className="admin-detail-value">{referralLinkError || 'Loading…'}</span>
                                            )}
                                            <p className="admin-referral-hint">
                                                Applications submitted through this link are assigned to you automatically.
                                            </p>
                                        </div>
                                    )}
                                    <div className="admin-profile-actions">
                                        <button
                                            className="admin-profile-action-btn"
                                            onClick={openChangePassword}
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                                                <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                                            </svg>
                                            Change Password
                                        </button>
                                        <button
                                            className="admin-profile-logout-btn"
                                            onClick={() => {
                                                setShowProfile(false);
                                                setShowLogoutWarning(true);
                                            }}
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                                                <polyline points="16 17 21 12 16 7"></polyline>
                                                <line x1="21" y1="12" x2="9" y2="12"></line>
                                            </svg>
                                            Logout
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </header>

                <main className="admin-dashboard-main">
                    <Outlet />
                </main>
            </div>

            {/* OTP step in front of every report export (utils/exportOtp.js) */}
            <ExportOtpProvider />

            {/* Change Password Modal */}
            {showChangePassword && (
                <div className="admin-modal-overlay" onClick={closeChangePassword}>
                    <div className="admin-modal-content change-password-modal" onClick={(e) => e.stopPropagation()}>
                        <h3>Change Password</h3>
                        <p>Update the password for your own account.</p>

                        {changePasswordError && (
                            <div className="change-password-error">{changePasswordError}</div>
                        )}

                        {changePasswordSuccess ? (
                            <>
                                <div className="change-password-success">{changePasswordSuccess}</div>
                                <div className="admin-modal-actions">
                                    <button className="admin-modal-btn confirm" onClick={closeChangePassword}>
                                        Done
                                    </button>
                                </div>
                            </>
                        ) : (
                            <form onSubmit={handleChangePassword} className="change-password-form">
                                <label>
                                    Current Password
                                    <input
                                        type="password"
                                        value={currentPassword}
                                        onChange={(e) => setCurrentPassword(e.target.value)}
                                        disabled={isChangingPassword}
                                        required
                                    />
                                </label>
                                <label>
                                    New Password
                                    <input
                                        type="password"
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        placeholder="At least 8 characters"
                                        disabled={isChangingPassword}
                                        required
                                    />
                                </label>
                                <label>
                                    Confirm New Password
                                    <input
                                        type="password"
                                        value={confirmNewPassword}
                                        onChange={(e) => setConfirmNewPassword(e.target.value)}
                                        disabled={isChangingPassword}
                                        required
                                    />
                                </label>
                                <div className="admin-modal-actions">
                                    <button type="button" className="admin-modal-btn cancel" onClick={closeChangePassword} disabled={isChangingPassword}>
                                        Cancel
                                    </button>
                                    <button type="submit" className="admin-modal-btn confirm" disabled={isChangingPassword}>
                                        {isChangingPassword ? 'Updating...' : 'Update Password'}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            )}

            {/* Logout Warning Modal */}
            {showLogoutWarning && (
                <div className="admin-modal-overlay">
                    <div className="admin-modal-content">
                        <div className="admin-modal-icon warning">⚠️</div>
                        <h3>Logout from Admin Portal?</h3>
                        <p>You will be logged out and redirected to the admin login page.</p>
                        <div className="admin-modal-actions">
                            <button className="admin-modal-btn cancel" onClick={cancelLogout}>
                                Cancel
                            </button>
                            <button className="admin-modal-btn confirm" onClick={confirmLogout}>
                                Logout
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AdminDashboardLayout;
